import { useState } from "react";
import type { RentabilidadeDoCliente } from "@bookdental/schemas";
import { porcentagem, reais } from "@/lib/dinheiro";
import { useIsMobile } from "@/lib/use-media-query";
import { cn } from "@/lib/cn";
import { moldura } from "../estilos";

const cor = (v: number) => (v >= 0 ? "text-emerald-600 dark:text-emerald-400" : "text-red-600 dark:text-red-400");

/** Margem de contribuição de cada clínica e, com o rateio, o resultado (que soma o do DRE). */
export function RentabilidadePorCliente({ clientes, materialRateado }: { clientes: RentabilidadeDoCliente[]; materialRateado: boolean }) {
  const [ratear, setRatear] = useState(false);
  const celular = useIsMobile();
  if (!clientes.length) return null;
  return (
    <section className={cn(moldura, "overflow-hidden")}>
      <div className="flex flex-wrap items-start justify-between gap-3 px-4 pt-4">
        <div>
          <h3 className="text-sm font-semibold text-zinc-900 dark:text-foreground">Rentabilidade por cliente</h3>
          <p className="mt-0.5 text-xs text-zinc-500 dark:text-muted-foreground">
            Margem = receita − material − imposto sobre o faturamento.
            {materialRateado && " Com o material pelas compras, ele entra rateado pela receita: para ver a diferença entre clientes, use o custo pela ficha técnica."}
          </p>
        </div>
        <label className="flex items-center gap-2 text-xs font-medium text-zinc-700 dark:text-foreground">
          <input type="checkbox" checked={ratear} onChange={(e) => setRatear(e.target.checked)} className="h-4 w-4 accent-brand-600" />
          Ratear o resto pela receita
        </label>
      </div>
      {celular ? (
        <ul className="mt-3 divide-y divide-zinc-100 dark:divide-border/50">
          {clientes.map((c) => (
            <li key={c.cliente} className="flex items-start justify-between gap-3 px-4 py-2.5 text-sm">
              <span className="min-w-0">
                <span className="block truncate font-medium text-zinc-900 dark:text-foreground">{c.nome}</span>
                <span className="block text-[11px] text-zinc-500 dark:text-muted-foreground">Receita {reais(c.receitaCents)} · margem {porcentagem(c.margemPct)}</span>
              </span>
              <span className={cn("shrink-0 tabular-nums font-semibold", cor(ratear ? c.resultadoCents : c.margemCents))}>{reais(ratear ? c.resultadoCents : c.margemCents)}</span>
            </li>
          ))}
        </ul>
      ) : (
        <div className="mt-3 overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="border-y border-zinc-200 text-left text-[11px] font-semibold uppercase tracking-wide text-zinc-500 dark:border-border dark:text-muted-foreground">
                <th className="px-4 py-2">Cliente</th>
                <th className="px-3 py-2 text-right">Receita</th>
                <th className="px-3 py-2 text-right">Material</th>
                <th className="px-3 py-2 text-right">Imposto</th>
                <th className="px-3 py-2 text-right">Margem</th>
                <th className="px-3 py-2 text-right">Margem %</th>
                {ratear && <th className="px-3 py-2 text-right">Rateio</th>}
                {ratear && <th className="px-4 py-2 text-right">Resultado</th>}
              </tr>
            </thead>
            <tbody>
              {clientes.map((c) => (
                <tr key={c.cliente} className="border-b border-zinc-100 dark:border-border/50">
                  <td className="px-4 py-2 font-medium text-zinc-900 dark:text-foreground">{c.nome}</td>
                  <td className="px-3 py-2 text-right tabular-nums">{reais(c.receitaCents)}</td>
                  <td className="px-3 py-2 text-right tabular-nums text-zinc-600 dark:text-foreground/70">{reais(-c.custoDiretoCents || 0)}</td>
                  <td className="px-3 py-2 text-right tabular-nums text-zinc-600 dark:text-foreground/70">{reais(-c.deducaoCents || 0)}</td>
                  <td className={cn("px-3 py-2 text-right tabular-nums font-semibold", cor(c.margemCents))}>{reais(c.margemCents)}</td>
                  <td className="px-3 py-2 text-right tabular-nums text-zinc-500 dark:text-muted-foreground">{porcentagem(c.margemPct)}</td>
                  {ratear && <td className="px-3 py-2 text-right tabular-nums text-zinc-600 dark:text-foreground/70">{reais(-c.rateioCents || 0)}</td>}
                  {ratear && <td className={cn("px-4 py-2 text-right tabular-nums font-semibold", cor(c.resultadoCents))}>{reais(c.resultadoCents)}</td>}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
      <div className="h-2" />
    </section>
  );
}
